import React from 'react';
import {Button, Link, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SlackIcon from "../../components/icons/SlackIcon";
import {SlackNotificationSettings, SlackOrganization} from "../../api/Model";
import {useSlackSettingsStore} from "../../api/SlackSettingsStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {Loading, LoadingData} from "../../components/loading-data/LoadingData";
import {SlackSettingsFormComponent} from "./SlackSettingsFormComponent";
import {SlackSettingsTable} from "./SlackSettingsTable";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useMessage} from "../../util/actions/Message";
import {usePermission} from "../../util/access/UseAccess";
import {SlackSettings_Admin} from "../../util/access/Permission";

export default function SlackSettingsPage() {
    const {
        remoteValue: settings,
        createSetting,
        updateSetting,
        deleteSetting,
        clearState,
        sendReport,
        organization,
    } = useSlackSettingsStore();
    const message = useMessage();
    const {canAdmin} = usePermission({canAdmin: SlackSettings_Admin});

    const handleCreate = useFormDialogAction({
        title: "Create Slack Notification Setting",
        description: "Configure automated reports to Slack for a specific site.",
        submitButtonText: 'Create',
        renderForm: SlackSettingsFormComponent,
        action: (openDialog) => openDialog({
            initialValue: {
                name: '',
                channelId: '',
                cron: '',
                problemTypes: [],
                enabled: true,
            },
            onSubmit: createSetting,
            successMessage: 'Created new Slack setting',
            errorMessage: 'Failed to create Slack setting.',
        })
    }, [createSetting]);

    const handleUpdate = useFormDialogAction({
        title: "Edit Slack Notification Setting",
        description: "Update the Slack notification configuration below.",
        submitButtonText: 'Update',
        renderForm: SlackSettingsFormComponent,
        action: (openDialog, setting: SlackNotificationSettings) => openDialog({
            initialValue: {
                name: setting.name,
                channelId: setting.channelId,
                cron: setting.cron,
                problemTypes: setting.problemTypes,
                enabled: setting.enabled,
            },
            onSubmit: form => updateSetting(setting, form),
            successMessage: 'Updated Slack setting',
            errorMessage: 'Failed to update Slack setting.',
        }),
    }, [updateSetting]);

    const handleDelete = useDeleteAction({
        description: 'Are you sure you want to delete this Slack notification setting?',
        action: deleteSetting,
        successMessage: 'Deleted Slack setting',
        failureMessage: 'Failed to delete Slack setting.',
    }, [deleteSetting]);

    const handleClearState = (settings: SlackNotificationSettings) => message.exec(
        () => clearState(settings),
        'State cleared successfully',
        'Failed to clear state',
    );

    const handleSendTest = async (settings: SlackNotificationSettings) => {
        try {
            const result = await sendReport(settings);
            switch (result.type) {
                case 'success':
                    message.success('Test notification sent successfully');
                    break;
                case 'error':
                    message.error(result.message);
                    break;
                default:
                    message.message(result.message);
                    break;
            }
        } catch (e) {
            message.error('Failed to send test notification', e);
        }
    };

    return (
        <Page
            header="Slack Notification Settings"
            counter={<StorePageCount value={settings}/>}
            description={
                <Stack direction="row" gap={1} alignItems="center" component="span">
                    Manage automated reports about problems with keys sent to Slack.
                </Stack>
            }
            actions={
                <Stack direction="row" gap={2}>
                    <Loading
                        value={organization}
                        skeletonValue={{id: '0', name: 'JetBrains', url: '#'}}
                    >{(organization: SlackOrganization) => (
                        <Button
                            component={Link}
                            variant="outlined"
                            startIcon={<SlackIcon colored/>}
                            href={organization.url}
                            target="_blank"
                        >Open {organization.name}</Button>
                    )}</Loading>
                    {canAdmin && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon/>}
                            onClick={handleCreate}
                        >
                            Add Setting
                        </Button>
                    )}
                </Stack>
            }
        >
            <LoadingData
                value={settings}
                emptyMessage="No Slack settings found."
                defaultErrorMessage="Failed to load Slack settings. Please try again later."
            >
                {resolved => (
                    <SlackSettingsTable
                        rows={resolved}
                        onEdit={handleUpdate}
                        onDelete={handleDelete}
                        onClearState={handleClearState}
                        onSendTest={handleSendTest}
                        canAdmin={canAdmin}
                    />
                )}
            </LoadingData>
        </Page>
    );
}
